import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  APP_GUIDE_VERSION,
  APP_SECTION_GUIDES,
  type AppSection,
  type AppSectionGuide,
} from '@financial/shared';
import { useSectionOnboarding } from '../../features/onboarding/useOnboarding';
import { Modal } from '../ui/Modal';

export function SectionGuide({ section }: { section: AppSection }) {
  const guide = APP_SECTION_GUIDES[section];
  const state = useSectionOnboarding(section, APP_GUIDE_VERSION);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const action = guide.action;
  const assistantAction = guide.assistantShortcut?.action;
  const complete = () => state.update.mutate('completed', { onSuccess: () => setOpen(false) });
  const continueToAction = (destination: string) => {
    setOpen(false);
    navigate(destination);
    state.update.mutate('completed');
  };

  return (
    <div className="mb-4">
      {state.loading ? null : !state.seen ? (
        <aside
          className="rounded-2xl border border-blue-100 bg-blue-50/80 p-2.5 text-slate-800 shadow-sm sm:p-4"
          aria-label={`Ayuda para ${guide.eyebrow}`}
        >
          <div className="flex items-center gap-2 sm:items-start sm:gap-3">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-base text-blue-700 shadow-sm sm:h-10 sm:w-10 sm:text-lg"
              aria-hidden
            >
              ?
            </span>
            <div className="min-w-0 flex-1">
              <div className="hidden sm:block">
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">
                  Guía rápida
                </p>
                <p className="mt-0.5 text-base font-bold leading-5 text-slate-900">{guide.title}</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">{guide.summary}</p>
              </div>
              <div className="flex min-w-0 items-center gap-2 sm:mt-3">
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="button button-primary min-w-0 flex-1 whitespace-nowrap px-2 text-xs sm:w-auto sm:flex-none sm:px-4 sm:text-sm"
                >
                  Ver cómo funciona
                </button>
                <button
                  type="button"
                  disabled={state.update.isPending}
                  onClick={() => state.update.mutate('dismissed')}
                  className="button button-ghost h-11 w-11 shrink-0 px-0 sm:w-auto sm:px-4"
                  aria-label={state.update.isPending ? 'Guardando elección' : 'Omitir esta guía'}
                >
                  <span className="sm:hidden" aria-hidden>
                    {state.update.isPending ? '…' : '×'}
                  </span>
                  <span className="sr-only sm:not-sr-only">
                    {state.update.isPending ? 'Guardando…' : 'Omitir esta guía'}
                  </span>
                </button>
              </div>
              {state.update.isError && (
                <p role="alert" className="mt-2 text-xs text-red-700">
                  No pudimos guardar tu elección. Puedes continuar usando la app.
                </p>
              )}
            </div>
          </div>
        </aside>
      ) : (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="tap-target inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
            aria-label={`Abrir ayuda de ${guide.eyebrow}`}
          >
            <span
              className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-xs"
              aria-hidden
            >
              ?
            </span>
            Ayuda
          </button>
        </div>
      )}
      <GuideModal
        guide={guide}
        open={open}
        onClose={() => setOpen(false)}
        onComplete={complete}
        onAction={action ? () => continueToAction(action.to) : undefined}
        onAssistantAction={assistantAction ? () => continueToAction(assistantAction.to) : undefined}
        pending={state.update.isPending}
        error={state.update.isError}
      />
    </div>
  );
}

export interface SectionGuideExtraSection {
  title: string;
  items: string[];
}

export function SectionGuideButton({
  section,
  className = '',
  openOnFirstVisit = false,
  extraSection,
}: {
  section: AppSection;
  className?: string;
  openOnFirstVisit?: boolean;
  extraSection?: SectionGuideExtraSection;
}) {
  const guide = APP_SECTION_GUIDES[section];
  const state = useSectionOnboarding(section, APP_GUIDE_VERSION);
  const [open, setOpen] = useState(false);
  const openedAutomatically = useRef(false);
  const navigate = useNavigate();
  const action = guide.action;
  const assistantAction = guide.assistantShortcut?.action;
  useEffect(() => {
    if (!openOnFirstVisit || state.loading || state.seen || openedAutomatically.current) return;
    openedAutomatically.current = true;
    setOpen(true);
  }, [openOnFirstVisit, state.loading, state.seen]);
  const complete = () => state.update.mutate('completed', { onSuccess: () => setOpen(false) });
  const continueToAction = (destination: string) => {
    setOpen(false);
    navigate(destination);
    state.update.mutate('completed');
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`tap-target inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 ${className}`}
        aria-label={`Abrir ayuda de ${guide.eyebrow}`}
      >
        <span
          className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-xs"
          aria-hidden
        >
          ?
        </span>
        <span className="hidden sm:inline">Ayuda</span>
      </button>
      <GuideModal
        guide={guide}
        extraSection={extraSection}
        open={open}
        onClose={() => setOpen(false)}
        onComplete={complete}
        onAction={action ? () => continueToAction(action.to) : undefined}
        onAssistantAction={assistantAction ? () => continueToAction(assistantAction.to) : undefined}
        pending={state.update.isPending}
        error={state.update.isError}
      />
    </>
  );
}

function GuideModal({
  guide,
  open,
  onClose,
  onComplete,
  onAction,
  onAssistantAction,
  pending,
  error,
  extraSection,
}: {
  guide: AppSectionGuide;
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  onAction?: () => void;
  onAssistantAction?: () => void;
  pending: boolean;
  error: boolean;
  extraSection?: SectionGuideExtraSection;
}) {
  return (
    <Modal open={open} title={guide.title} eyebrow="Ayuda de esta sección" onClose={onClose}>
      <div className="space-y-5">
        <p className="text-base leading-7 text-slate-700">{guide.summary}</p>
        {guide.assistantShortcut && (
          <section className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
            <h3 className="text-sm font-bold text-slate-900">{guide.assistantShortcut.title}</h3>
            <p className="mt-1 text-sm leading-6 text-slate-700">
              {guide.assistantShortcut.description}
            </p>
            {guide.assistantShortcut.note && (
              <p className="mt-2 text-xs leading-5 text-slate-600">
                {guide.assistantShortcut.note}
              </p>
            )}
            <button
              type="button"
              onClick={onAssistantAction}
              disabled={pending}
              className="button button-primary mt-3 w-full sm:w-auto"
            >
              {guide.assistantShortcut.action.label}
            </button>
          </section>
        )}
        <section>
          <h3 className="text-sm font-bold text-slate-900">En palabras simples</h3>
          <ul className="mt-2 space-y-2">
            {guide.keyIdeas.map((idea) => (
              <li key={idea} className="flex gap-2 text-sm leading-6 text-slate-600">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" aria-hidden />
                <span>{idea}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h3 className="text-sm font-bold text-slate-900">Qué puedes hacer</h3>
          <ol className="mt-2 space-y-2">
            {guide.steps.map((step, index) => (
              <li key={step} className="flex gap-3 text-sm leading-6 text-slate-600">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>
        {extraSection && extraSection.items.length > 0 && (
          <section className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4">
            <h3 className="text-sm font-bold text-slate-900">{extraSection.title}</h3>
            <ul className="mt-2 space-y-2">
              {extraSection.items.map((item) => (
                <li key={item} className="flex gap-2 text-sm leading-6 text-slate-700">
                  <span className="mt-2 text-emerald-700" aria-hidden>
                    ✓
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
        {error && (
          <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-800">
            No pudimos guardar tu avance. Revisa tu conexión y vuelve a intentarlo.
          </p>
        )}
        <div className="modal-action-bar flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {guide.action && (
            <button
              type="button"
              onClick={onAction}
              disabled={pending}
              className="button button-secondary w-full sm:w-auto"
            >
              {guide.action.label}
            </button>
          )}
          <button
            type="button"
            onClick={onComplete}
            disabled={pending}
            className="button button-primary w-full sm:w-auto"
          >
            {pending ? 'Guardando…' : 'Entendido'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
